import * as hashwasm from 'hash-wasm'
import { BlobUploadClient } from '../api/clients/blobs/BlobUploadClient'

export const blobToBase64 = (blob: Blob) => {
	return new Promise((resolve) => {
		const reader = new FileReader()
		reader.readAsDataURL(blob)
		reader.onloadend = function () {
			resolve(reader.result)
		}
	})
}


export const dataUrlToBlob = (dataUrl: string) => {
	// get the first part of data url, includes the mime type
	const splitDataUrl = dataUrl.split(',')

	const mimeTypeMatch = splitDataUrl[0].match(/:(.*);/)

	if (mimeTypeMatch === null)
		throw new Error('could not get mime type for data url')

	const mimeType = mimeTypeMatch[1]

	// have to convert the data url to uint8array

	const byteString = window.atob(splitDataUrl[1])
	let byteStringLength = byteString.length
	const uint8array = new Uint8Array(byteStringLength)

	while (byteStringLength--)
		uint8array[byteStringLength] = byteString.charCodeAt(byteStringLength)

	return new Blob([uint8array], { type: mimeType })
}

export const uploadBlob = async (fileReader: FileReader, file: File, blobName?: string) => {
	if (file === undefined) return

	const blobUploadClient = new BlobUploadClient()

	// if the file is < 30MB we don't have to worry about chunking, just upload normally
	if (Math.round(file.size / (1024 * 1024)) < 30) {
		try {
			const { data: blobId } = await blobUploadClient.uploadBlob(file, blobName)
			return blobId
		} catch (e) {
			console.log(e)
			return 0
		}
	}

	try {
		// hash the original file to make sure the file is uploaded correctly
		const fileHash = await hashFile(fileReader, file)
		if (fileHash === undefined)
			throw new Error('could not hash uploaded file')

		const { data: uploadResponse } =
			await blobUploadClient.createBlobForChunkedUpload(file, fileHash, blobName)

		for (let i = 0; i < uploadResponse.chunkCount; i++) {
			const offset = i * uploadResponse.chunkSize

			const chunk = file.slice(offset, offset + uploadResponse.chunkSize)
			await blobUploadClient.uploadBlobChunk(
				i,
				uploadResponse.blobId,
				new Blob([chunk]),
			)
		}

		await blobUploadClient.commitUpload(uploadResponse.blobId)

		return uploadResponse.blobId
	} catch (e) {
		console.log(e)
		return 0
	}
}

// hash the file in chunks :)
// https://stackoverflow.com/questions/768268/how-to-calculate-md5-hash-of-a-file-using-javascript/63287199#63287199
const hashFile = async (fileReader: FileReader, file: File) => {
	if (file === undefined) return

	let hasher = await hashwasm.createMD5()

	const hashChunk = (chunk: Blob) => {
		return new Promise<void>((resolve) => {
			fileReader.onload = (e) => {
				const view = new Uint8Array(e.target!.result as ArrayBuffer)
				hasher?.update(view)
				resolve()
			}

			fileReader.readAsArrayBuffer(chunk)
		})
	}

	if (hasher) hasher.init()
	else hasher = await hashwasm.createMD5()

	const hashChunkSize = 64 * 1024 * 1024

	const hashChunkNumber = Math.floor(file.size / hashChunkSize)

	for (let i = 0; i <= hashChunkNumber; i++) {
		const chunk = file.slice(
			hashChunkSize * i,
			Math.min(hashChunkSize * (i + 1), file.size),
		)
		await hashChunk(chunk)
	}

	const hash = hasher.digest()
	return Promise.resolve(hash)
}
